import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Users, MagnifyingGlass, Copy, Eye, EyeSlash, ArrowSquareOut,
  EnvelopeSimple, Phone, IdentificationCard, Key, GraduationCap,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";

function fmtCpf(d) {
  const s = (d || "").replace(/\D/g, "");
  if (s.length !== 11) return d || "—";
  return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
}

export default function MyMembers() {
  const [data, setData] = useState({ members: [], funnels: [], member_login_url: "/member/login" });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [reveal, setReveal] = useState({}); // { memberId: true }

  const loginUrl = publicUrl(data.member_login_url || "/member/login");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/leads/my-members");
      setData(d);
    } catch (e) {
      toast.error(errMsg(e, "Erro ao carregar"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return data.members;
    return data.members.filter((m) => {
      return (
        (m.name || "").toLowerCase().includes(term) ||
        (m.email || "").toLowerCase().includes(term) ||
        (m.phone || "").toLowerCase().includes(term) ||
        (m.cpf || "").includes(term.replace(/\D/g, ""))
      );
    });
  }, [q, data.members]);

  const copyOne = (val, label) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success(`${label} copiado`);
  };

  const copyFullAccess = (m) => {
    const txt = `MercadoPagu — Acesso à área de membro\n\nLink: ${loginUrl}\nE-mail: ${m.email}\nSenha: ${m.plain_password || "(o membro criou uma nova)"}`;
    navigator.clipboard.writeText(txt);
    toast.success("Acesso completo copiado");
  };

  const toggleReveal = (id) => setReveal((r) => ({ ...r, [id]: !r[id] }));

  return (
    <div className="fadeUp max-w-6xl" data-testid="my-members-page">
      <div className="label-caps mb-2 flex items-center gap-1">
        <GraduationCap size={12} /> Área do produtor
      </div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mb-2">
        Meus Membros
      </h1>
      <p className="text-white/60 text-sm mb-8 max-w-3xl">
        Membros que compraram nos seus funis. Você vê o <b>login e senha</b> que cada um criou no cadastro — útil pra reenviar acesso via WhatsApp/e-mail caso o cliente perca.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:gap-4 mb-6">
        <div className="card-flat p-4">
          <div className="label-caps text-white/50">Total de membros</div>
          <div className="font-display font-black text-3xl mt-1" data-testid="stat-members">{data.members.length}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-caps text-white/50">Funis publicados</div>
          <div className="font-display font-black text-3xl mt-1 text-[var(--accent)]" data-testid="stat-funnels">{data.funnels.length}</div>
        </div>
      </div>

      {/* Link do escritório (compartilhável) */}
      <div className="card-flat p-4 mb-6 border-l-2 border-l-[var(--accent)]" data-testid="office-link-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm bg-[var(--accent)]/15 flex items-center justify-center shrink-0">
            <ArrowSquareOut size={18} weight="bold" color="var(--accent)" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="label-caps text-white/50">Link único para todos os seus membros</div>
            <div className="font-mono text-sm truncate">{loginUrl}</div>
          </div>
          <button
            onClick={() => copyOne(loginUrl, "Link")}
            data-testid="copy-office-link"
            className="btn-ghost text-xs flex items-center gap-1 shrink-0"
          >
            <Copy size={13} /> Copiar
          </button>
        </div>
      </div>

      {/* Busca */}
      <div className="relative mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, e-mail, telefone ou CPF"
          data-testid="member-search"
          className="w-full pl-10 pr-4 py-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-sm text-sm focus:border-[var(--accent)] outline-none"
        />
      </div>

      {/* Lista */}
      {loading ? (
        <div className="card-flat p-10 text-center text-white/40" data-testid="loading">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-10 text-center" data-testid="empty">
          <Users size={40} className="mx-auto text-white/30 mb-3" />
          <div className="text-sm text-white/60">
            {q ? "Nenhum membro encontrado com esse termo." : "Nenhum membro cadastrado ainda. Compartilhe seus funis para começar a receber vendas."}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => {
            const shown = reveal[m.id];
            return (
              <div key={m.id} className="card-flat p-5 fadeUp" data-testid={`member-${m.id}`}>
                <div className="grid md:grid-cols-[1fr_auto] gap-4 items-start">
                  {/* dados básicos */}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-[var(--accent)]/20 text-[var(--accent)] flex items-center justify-center shrink-0">
                        <Users size={14} weight="bold" />
                      </div>
                      <div className="font-display font-bold text-lg truncate">{m.name || "—"}</div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-white/70 pl-10">
                      <div className="flex items-center gap-1.5"><EnvelopeSimple size={11} className="text-white/40 shrink-0" /> <span className="truncate">{m.email}</span></div>
                      {m.phone && <div className="flex items-center gap-1.5"><Phone size={11} className="text-white/40 shrink-0" /> {m.phone}</div>}
                      {m.cpf && <div className="flex items-center gap-1.5 font-mono"><IdentificationCard size={11} className="text-white/40 shrink-0" /> {fmtCpf(m.cpf)}</div>}
                      {m.funnel_title && <div className="flex items-center gap-1.5"><GraduationCap size={11} className="text-white/40 shrink-0" /> <span className="truncate">{m.funnel_title}</span></div>}
                    </div>
                  </div>
                </div>

                {/* Credenciais */}
                <div className="mt-4 pt-4 border-t border-white/5 grid md:grid-cols-[1fr_auto] gap-3 items-center">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="label-caps flex items-center gap-1 text-white/40"><Key size={10} /> Senha do membro</span>
                    <code className="bg-black/40 px-3 py-1.5 rounded-sm font-mono border border-white/10">
                      {m.plain_password
                        ? (shown ? m.plain_password : "•".repeat(Math.min(m.plain_password.length, 12)))
                        : <span className="text-white/40 italic">(sem senha inicial salva)</span>}
                    </code>
                    {m.plain_password && (
                      <>
                        <button
                          onClick={() => toggleReveal(m.id)}
                          data-testid={`reveal-${m.id}`}
                          className="text-white/50 hover:text-white"
                          title={shown ? "Ocultar" : "Mostrar"}
                        >
                          {shown ? <EyeSlash size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => copyOne(m.plain_password, "Senha")}
                          data-testid={`copy-pw-${m.id}`}
                          className="text-white/50 hover:text-white"
                          title="Copiar senha"
                        >
                          <Copy size={13} />
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={() => copyFullAccess(m)}
                    data-testid={`copy-all-${m.id}`}
                    className="btn-ghost text-xs flex items-center gap-1 justify-self-end"
                  >
                    <Copy size={12} /> Copiar acesso completo
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
